import { useState, useEffect } from "react";
import { X, Save, Trash2, Plus, Anchor, Users, Dog, MapPin, Calendar, StickyNote, History, Camera, Image as ImageIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fileToAttachment } from "@/lib/imageUtil";
import VesselLinkPicker from "@/components/VesselLinkPicker";

const EMPTY_PERSON = { name: "", phone: "", whatsapp: "", instagram: "", facebook: "", email: "" };
const EMPTY_PET = { name: "", breed: "", description: "" };

const EMPTY_CONTACT = {
    boat_name: "",
    boat_type: "",
    boat_length_ft: "",
    hull_color_primary: "",
    hull_color_trim: "",
    home_port: "",
    where_met: "",
    when_met: "",
    notes: "",
    people: [{ ...EMPTY_PERSON }],
    pets: [],
    boat_photo: "",
    boat_photo_filename: "",
    linked_vessel_id: null,
    linked_profile: null,
};

export default function CruiserContactModal({ open, contact, onClose, onSaved, onDeleted }) {
    const [form, setForm] = useState(EMPTY_CONTACT);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [encounters, setEncounters] = useState([]);
    const [encountersLoading, setEncountersLoading] = useState(false);
    // Tracks whether the photo has been touched in this session (uploaded a new
    // one OR removed an existing one). When false, we skip sending the photo
    // bytes back to the server on PUT — saves a ~900KB upload on every edit,
    // critical on slow Starlink Roam where the full payload hangs.
    const [photoDirty, setPhotoDirty] = useState(false);

    const isEdit = !!contact?.id;

    useEffect(() => {
        if (open) {
            setErr("");
            setConfirmDelete(false);
            setPhotoDirty(false);  // reset every time the modal opens
            if (contact) {
                // Server returns `null` for empty text fields (notes, notes, home_port,
                // etc). Spreading `...contact` after `...EMPTY_CONTACT` overwrites the
                // empty-string defaults with null, which then crashes cleanForSave when
                // it calls .trim() on those nulls. Coalesce every string field back to ""
                // to keep the form state uniformly stringly-typed.
                const stringify = (v) => (v == null ? "" : String(v));
                setForm({
                    ...EMPTY_CONTACT,
                    ...contact,
                    boat_name: stringify(contact.boat_name),
                    boat_type: stringify(contact.boat_type),
                    boat_length_ft: contact.boat_length_ft ?? "",
                    hull_color_primary: stringify(contact.hull_color_primary),
                    hull_color_trim: stringify(contact.hull_color_trim),
                    home_port: stringify(contact.home_port),
                    where_met: stringify(contact.where_met),
                    when_met: stringify(contact.when_met),
                    notes: stringify(contact.notes),
                    people: contact.people?.length ? contact.people : [{ ...EMPTY_PERSON }],
                    pets: contact.pets || [],
                    boat_photo: contact.boat_photo || "",
                    boat_photo_filename: contact.boat_photo_filename || "",
                    linked_vessel_id: contact.linked_vessel_id || null,
                    linked_profile: contact.linked_profile || null,
                });
            } else {
                setForm({ ...EMPTY_CONTACT, people: [{ ...EMPTY_PERSON }], pets: [] });
            }
            setEncounters([]);
        }
    }, [open, contact]);

    // Load encounters when editing an existing contact.
    useEffect(() => {
        if (!open || !contact?.id) return;
        let alive = true;
        setEncountersLoading(true);
        (async () => {
            try {
                const res = await api.get(`/cruiser-contacts/${contact.id}/encounters`);
                if (alive) setEncounters(res.data || []);
            } catch (_e) {
                // Non-fatal — encounters are read-only signal, don't block editing.
                if (alive) setEncounters([]);
            } finally {
                if (alive) setEncountersLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [open, contact?.id]);

    if (!open) return null;

    const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const setPersonField = (idx, key, val) => {
        setForm((f) => ({
            ...f,
            people: f.people.map((p, i) => (i === idx ? { ...p, [key]: val } : p)),
        }));
    };

    const addPerson = () => setForm((f) => ({ ...f, people: [...f.people, { ...EMPTY_PERSON }] }));
    const removePerson = (idx) => setForm((f) => ({
        ...f,
        people: f.people.length > 1 ? f.people.filter((_, i) => i !== idx) : [{ ...EMPTY_PERSON }],
    }));

    const setPetField = (idx, key, val) => {
        setForm((f) => ({
            ...f,
            pets: f.pets.map((p, i) => (i === idx ? { ...p, [key]: val } : p)),
        }));
    };
    const addPet = () => setForm((f) => ({ ...f, pets: [...f.pets, { ...EMPTY_PET }] }));
    const removePet = (idx) => setForm((f) => ({ ...f, pets: f.pets.filter((_, i) => i !== idx) }));

    const cleanForSave = () => {
        // Defensive `?? ""` on every string field: even though setForm coalesces
        // null → "" on load, this second layer means a future code path that
        // pokes null into state (e.g. an autofill helper) can't crash the save.
        const s = (v) => (v == null ? "" : String(v)).trim();
        const cleanPerson = (p) => {
            const cleaned = {};
            ["name", "phone", "whatsapp", "instagram", "facebook", "email"].forEach((k) => {
                const v = s(p[k]);
                if (v) cleaned[k] = v;
            });
            return cleaned.name ? cleaned : null;
        };
        const cleanPet = (p) => {
            const cleaned = {};
            ["name", "breed", "description"].forEach((k) => {
                const v = s(p[k]);
                if (v) cleaned[k] = v;
            });
            return cleaned.name ? cleaned : null;
        };
        return {
            boat_name: s(form.boat_name),
            boat_type: s(form.boat_type) || null,
            boat_length_ft: form.boat_length_ft === "" || form.boat_length_ft == null ? null : Number(form.boat_length_ft),
            hull_color_primary: s(form.hull_color_primary) || null,
            hull_color_trim: s(form.hull_color_trim) || null,
            home_port: s(form.home_port) || null,
            where_met: s(form.where_met) || null,
            when_met: s(form.when_met) || null,
            notes: s(form.notes) || null,
            people: form.people.map(cleanPerson).filter(Boolean),
            pets: form.pets.map(cleanPet).filter(Boolean),
            boat_photo: form.boat_photo || null,
            boat_photo_filename: form.boat_photo_filename || null,
            linked_vessel_id: form.linked_vessel_id || null,
        };
    };

    const onPhotoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr("");
        setBusy(true);
        try {
            // Resize + compress in the browser so we don't ship a 12 MB upload.
            // Same helper used by entry photos — keeps under ~900 KB.
            const att = await fileToAttachment(file, { maxDim: 1400, quality: 0.82 });
            setForm((s) => ({ ...s, boat_photo: att.data, boat_photo_filename: att.filename || file.name }));
            setPhotoDirty(true);
        } catch (e2) {
            setErr("Couldn't process that image: " + (e2.message || "unknown error"));
        } finally {
            setBusy(false);
            // Reset the input so picking the same file twice still triggers onChange
            e.target.value = "";
        }
    };

    const removePhoto = () => {
        setForm((s) => ({ ...s, boat_photo: "", boat_photo_filename: "" }));
        setPhotoDirty(true);
    };

    const save = async (e) => {
        e.preventDefault();
        setErr("");
        if (!(form.boat_name || "").trim()) {
            setErr("Boat name is required — it's the anchor of the whole record.");
            return;
        }
        setBusy(true);
        try {
            let payload = cleanForSave();
            // EDIT path: only send fields that could have changed. Critically,
            // omit the boat_photo (huge base64) unless the captain actually
            // touched it this session. Saves ~900KB of upload on every edit,
            // which was hanging on slow Starlink Roam links.
            if (isEdit && !photoDirty) {
                const { boat_photo, boat_photo_filename, ...rest } = payload;
                payload = rest;
            }
            // Diagnostic logging when the payload is still photo-heavy.
            const photoBytes = payload.boat_photo ? payload.boat_photo.length : 0;
            if (photoBytes > 400_000) {
                console.info(`[cruiser] saving with ${Math.round(photoBytes / 1024)}KB photo — may be slow on cellular`);
            }
            const res = isEdit
                ? await api.put(`/cruiser-contacts/${contact.id}`, payload)
                : await api.post("/cruiser-contacts", payload);
            onSaved?.(res.data);
            setBusy(false);  // defensive — if onClose fails for any reason, button isn't stuck
            onClose();
        } catch (e2) {
            // Surface a useful message for the 4 common failure modes:
            //   1. Network timeout (axios `ECONNABORTED`) — usually slow upload of boat photo
            //   2. Backend HTTP error WITH `detail` field — use it verbatim
            //   3. Backend HTTP error WITHOUT usable detail — surface the status code
            //      (previously we fell through to a generic "Something went wrong"
            //      which masked 402 subscription errors, 502 gateway errors, etc.)
            //   4. Browser-level network error (no response object) — surface axios's msg
            console.error("[cruiser] save failed", {
                status: e2.response?.status,
                data: e2.response?.data,
                message: e2.message,
                code: e2.code,
            });
            let msg;
            if (e2.code === "ECONNABORTED" || /timeout/i.test(e2.message || "")) {
                msg = photoDirty
                    ? "Save took too long. The new boat photo may be too large for this connection. Try a smaller photo or save the rest first without it."
                    : "Save took too long. Check your connection and try again.";
            } else {
                const detail = e2.response?.data?.detail;
                if (detail) {
                    msg = formatApiErrorDetail(detail);
                } else if (e2.response?.status) {
                    // Server responded but with no `detail` — most helpful thing
                    // we can show is the HTTP status. 402 = subscription lapse,
                    // 5xx = server error, 429 = rate limit, etc.
                    msg = `Couldn't save (server returned ${e2.response.status}). Please try again${e2.response.status >= 500 ? " in a moment" : ""}.`;
                } else {
                    // No response object → network/CORS/DNS failure
                    msg = e2.message || "Couldn't save. Please try again.";
                }
            }
            setErr(msg);
            setBusy(false);
        }
    };

    const doDelete = async () => {
        if (!isEdit) return;
        setBusy(true);
        try {
            await api.delete(`/cruiser-contacts/${contact.id}`);
            onDeleted?.(contact.id);
            onClose();
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message || "Couldn't delete.");
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="cruiser-contact-modal"
            onClick={onClose}
            style={{
                position: "fixed", inset: 0,
                background: "rgba(8, 14, 24, 0.72)",
                backdropFilter: "blur(4px)",
                display: "grid", placeItems: "center",
                zIndex: 250, padding: 16,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bm-card"
                style={{
                    maxWidth: 640, width: "100%",
                    maxHeight: "calc(100vh - 32px)",
                    overflowY: "auto",
                    padding: 0,
                    border: "1px solid var(--bm-gold-dim)",
                    boxShadow: "0 24px 70px -20px rgba(0,0,0,0.6)",
                }}
            >
                {/* Header */}
                <div style={{
                    padding: "22px 24px 14px",
                    borderBottom: "1px solid var(--bm-border-soft)",
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14,
                    position: "sticky", top: 0, background: "var(--bm-card-bg, #0f1a2c)", zIndex: 1,
                }}>
                    <div>
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                            color: "var(--bm-gold)", marginBottom: 6,
                        }}>
                            <Anchor size={12} /> Cruiser contact
                        </div>
                        <h2 className="bm-serif" style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>
                            {isEdit ? form.boat_name || "Edit contact" : "New contact"}
                        </h2>
                    </div>
                    <button
                        data-testid="cruiser-contact-close-btn"
                        type="button" onClick={onClose} aria-label="Close"
                        className="bm-btn bm-btn-ghost"
                        style={{ padding: "6px 8px" }}
                    >
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={save} style={{ padding: "20px 24px 24px" }}>
                    {/* Boat photo — visual identity, sits at the very top */}
                    <SectionHeader icon={<Camera size={13} />} label="Boat photo" />
                    <div data-testid="cc-boat-photo-section" style={{ marginBottom: 18 }}>
                        {form.boat_photo ? (
                            <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--bm-gold-dim)" }}>
                                <img
                                    data-testid="cc-boat-photo-preview"
                                    src={form.boat_photo}
                                    alt={form.boat_name || "Boat photo"}
                                    style={{ display: "block", width: "100%", maxHeight: 220, objectFit: "cover" }}
                                />
                                <button
                                    type="button"
                                    data-testid="cc-boat-photo-remove-btn"
                                    onClick={removePhoto}
                                    aria-label="Remove photo"
                                    className="bm-btn"
                                    style={{
                                        position: "absolute", top: 8, right: 8,
                                        padding: "5px 9px", fontSize: 11,
                                        background: "rgba(0, 0, 0, 0.6)",
                                        color: "#fff",
                                        border: "1px solid rgba(255, 255, 255, 0.3)",
                                        backdropFilter: "blur(4px)",
                                    }}
                                >
                                    <X size={11} /> Remove
                                </button>
                            </div>
                        ) : (
                            <label
                                data-testid="cc-boat-photo-empty"
                                style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", justifyContent: "center",
                                    gap: 8,
                                    padding: "26px 16px",
                                    borderRadius: 10,
                                    border: "1px dashed var(--bm-border-soft)",
                                    background: "rgba(255, 255, 255, 0.02)",
                                    cursor: "pointer",
                                    transition: "border-color 0.15s, background 0.15s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = "var(--bm-gold-dim)";
                                    e.currentTarget.style.background = "rgba(200, 162, 92, 0.04)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "var(--bm-border-soft)";
                                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                                }}
                            >
                                <ImageIcon size={22} style={{ color: "var(--bm-gold)" }} />
                                <span style={{ fontSize: 13, color: "var(--bm-text-muted)" }}>
                                    Add a photo of the boat
                                </span>
                                <span style={{ fontSize: 11, color: "var(--bm-text-dim)" }}>
                                    Helps you recognize them next season
                                </span>
                                <input
                                    data-testid="cc-boat-photo-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={onPhotoChange}
                                    style={{ display: "none" }}
                                />
                            </label>
                        )}
                    </div>

                    {/* Boat */}
                    <SectionHeader icon={<Anchor size={13} />} label="Boat details" />

                    <div data-testid="cc-vessel-link-section" style={{ marginBottom: 14 }}>
                        <VesselLinkPicker
                            value={{ linked_vessel_id: form.linked_vessel_id, linked_profile: form.linked_profile }}
                            initialQuery={form.boat_name}
                            onChange={({ linked_vessel_id, linked_profile_preview }) => {
                                setForm((s) => {
                                    const next = { ...s, linked_vessel_id, linked_profile: linked_profile_preview };
                                    // If user just linked a vessel and our boat_name is empty,
                                    // pre-fill from the linked profile.
                                    if (linked_profile_preview) {
                                        if (!s.boat_name?.trim()) next.boat_name = linked_profile_preview.name || "";
                                        if (!s.home_port?.trim() && linked_profile_preview.public_hailing_port) {
                                            next.home_port = linked_profile_preview.public_hailing_port;
                                        }
                                    }
                                    return next;
                                });
                            }}
                        />
                    </div>

                    <label className="bm-label">Boat name *</label>
                    <input
                        data-testid="cc-boat-name-input"
                        className="bm-input"
                        value={form.boat_name}
                        onChange={(e) => setField("boat_name", e.target.value)}
                        placeholder="Shamrock"
                        style={{ marginBottom: 12 }}
                        autoFocus
                        required
                    />

                    <div style={twoCol}>
                        <div>
                            <label className="bm-label">Type / model</label>
                            <input
                                data-testid="cc-boat-type-input"
                                className="bm-input"
                                value={form.boat_type}
                                onChange={(e) => setField("boat_type", e.target.value)}
                                placeholder="42ft sailboat"
                            />
                        </div>
                        <div>
                            <label className="bm-label">Length (ft)</label>
                            <input
                                data-testid="cc-boat-length-input"
                                type="number"
                                inputMode="decimal"
                                className="bm-input"
                                value={form.boat_length_ft}
                                onChange={(e) => setField("boat_length_ft", e.target.value)}
                                placeholder="42"
                            />
                        </div>
                    </div>

                    <div style={twoCol}>
                        <div>
                            <label className="bm-label">Hull colour</label>
                            <input
                                data-testid="cc-hull-primary-input"
                                className="bm-input"
                                value={form.hull_color_primary}
                                onChange={(e) => setField("hull_color_primary", e.target.value)}
                                placeholder="White"
                            />
                        </div>
                        <div>
                            <label className="bm-label">Trim colour</label>
                            <input
                                data-testid="cc-hull-trim-input"
                                className="bm-input"
                                value={form.hull_color_trim}
                                onChange={(e) => setField("hull_color_trim", e.target.value)}
                                placeholder="Green"
                            />
                        </div>
                    </div>

                    <label className="bm-label">Home port</label>
                    <input
                        data-testid="cc-home-port-input"
                        className="bm-input"
                        value={form.home_port}
                        onChange={(e) => setField("home_port", e.target.value)}
                        placeholder="Sidney, BC"
                        style={{ marginBottom: 16 }}
                    />

                    {/* People */}
                    <SectionHeader icon={<Users size={13} />} label={`People (${form.people.length})`} />
                    {form.people.map((p, idx) => (
                        <div key={idx} style={subCardStyle} data-testid={`cc-person-${idx}`}>
                            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                                <input
                                    data-testid={`cc-person-name-${idx}`}
                                    className="bm-input"
                                    value={p.name}
                                    onChange={(e) => setPersonField(idx, "name", e.target.value)}
                                    placeholder="Name (e.g. John)"
                                    style={{ flex: 1 }}
                                />
                                {form.people.length > 1 && (
                                    <button
                                        data-testid={`cc-person-remove-${idx}`}
                                        type="button"
                                        onClick={() => removePerson(idx)}
                                        className="bm-btn bm-btn-ghost"
                                        style={{ padding: "0 10px" }}
                                        aria-label="Remove person"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                            <div style={twoCol}>
                                <input
                                    data-testid={`cc-person-phone-${idx}`}
                                    className="bm-input"
                                    value={p.phone}
                                    onChange={(e) => setPersonField(idx, "phone", e.target.value)}
                                    placeholder="Phone"
                                />
                                <input
                                    data-testid={`cc-person-whatsapp-${idx}`}
                                    className="bm-input"
                                    value={p.whatsapp}
                                    onChange={(e) => setPersonField(idx, "whatsapp", e.target.value)}
                                    placeholder="WhatsApp"
                                />
                            </div>
                            <div style={twoCol}>
                                <input
                                    data-testid={`cc-person-instagram-${idx}`}
                                    className="bm-input"
                                    value={p.instagram}
                                    onChange={(e) => setPersonField(idx, "instagram", e.target.value)}
                                    placeholder="Instagram @handle"
                                />
                                <input
                                    data-testid={`cc-person-facebook-${idx}`}
                                    className="bm-input"
                                    value={p.facebook}
                                    onChange={(e) => setPersonField(idx, "facebook", e.target.value)}
                                    placeholder="Facebook"
                                />
                            </div>
                            <input
                                data-testid={`cc-person-email-${idx}`}
                                className="bm-input"
                                type="email"
                                value={p.email}
                                onChange={(e) => setPersonField(idx, "email", e.target.value)}
                                placeholder="Email"
                            />
                        </div>
                    ))}
                    <button
                        data-testid="cc-add-person-btn"
                        type="button"
                        onClick={addPerson}
                        className="bm-btn bm-btn-ghost"
                        style={{ marginBottom: 16, fontSize: 13 }}
                    >
                        <Plus size={13} /> Add another person
                    </button>

                    {/* Pets */}
                    <SectionHeader icon={<Dog size={13} />} label={`Pets (${form.pets.length})`} />
                    {form.pets.length === 0 && (
                        <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--bm-text-dim)" }}>
                            No pets yet. Boats with chocolate labradoodles welcomed. 🐕
                        </p>
                    )}
                    {form.pets.map((p, idx) => (
                        <div key={idx} style={subCardStyle} data-testid={`cc-pet-${idx}`}>
                            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                <input
                                    data-testid={`cc-pet-name-${idx}`}
                                    className="bm-input"
                                    value={p.name}
                                    onChange={(e) => setPetField(idx, "name", e.target.value)}
                                    placeholder="Pet name (e.g. Coho)"
                                    style={{ flex: 1 }}
                                />
                                <button
                                    data-testid={`cc-pet-remove-${idx}`}
                                    type="button"
                                    onClick={() => removePet(idx)}
                                    className="bm-btn bm-btn-ghost"
                                    style={{ padding: "0 10px" }}
                                    aria-label="Remove pet"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div style={twoCol}>
                                <input
                                    data-testid={`cc-pet-breed-${idx}`}
                                    className="bm-input"
                                    value={p.breed}
                                    onChange={(e) => setPetField(idx, "breed", e.target.value)}
                                    placeholder="Breed (labradoodle)"
                                />
                                <input
                                    data-testid={`cc-pet-description-${idx}`}
                                    className="bm-input"
                                    value={p.description}
                                    onChange={(e) => setPetField(idx, "description", e.target.value)}
                                    placeholder="Description (chocolate)"
                                />
                            </div>
                        </div>
                    ))}
                    <button
                        data-testid="cc-add-pet-btn"
                        type="button"
                        onClick={addPet}
                        className="bm-btn bm-btn-ghost"
                        style={{ marginBottom: 16, fontSize: 13 }}
                    >
                        <Plus size={13} /> Add a pet
                    </button>

                    {/* Where + when met */}
                    <SectionHeader icon={<MapPin size={13} />} label="Where & when you met" />
                    <div style={twoCol}>
                        <div>
                            <label className="bm-label">Where</label>
                            <input
                                data-testid="cc-where-met-input"
                                className="bm-input"
                                value={form.where_met}
                                onChange={(e) => setField("where_met", e.target.value)}
                                placeholder="Princess Louisa, BC"
                            />
                        </div>
                        <div>
                            <label className="bm-label">When</label>
                            <input
                                data-testid="cc-when-met-input"
                                className="bm-input"
                                value={form.when_met}
                                onChange={(e) => setField("when_met", e.target.value)}
                                placeholder="Aug 2025"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <SectionHeader icon={<StickyNote size={13} />} label="Notes" />
                    <textarea
                        data-testid="cc-notes-input"
                        className="bm-input"
                        rows={3}
                        value={form.notes}
                        onChange={(e) => setField("notes", e.target.value)}
                        placeholder="Anything you want to remember next time you cross paths…"
                        style={{ resize: "vertical", marginBottom: 16 }}
                    />

                    {isEdit && (encounters.length > 0 || encountersLoading) && (
                        <div data-testid="cc-encounters-section" style={{ marginBottom: 16 }}>
                            <SectionHeader icon={<History size={13} />} label={`Encounters (${encounters.length})`} />
                            {encountersLoading && (
                                <p style={{ margin: 0, fontSize: 12, color: "var(--bm-text-dim)" }}>Loading encounters…</p>
                            )}
                            {!encountersLoading && encounters.map((e) => (
                                <div
                                    key={e.entry_id}
                                    data-testid={`cc-encounter-${e.entry_id}`}
                                    style={{
                                        padding: "8px 12px",
                                        marginBottom: 6,
                                        borderLeft: "2px solid var(--bm-gold)",
                                        background: "rgba(255, 255, 255, 0.02)",
                                        borderRadius: "0 6px 6px 0",
                                        fontSize: 13,
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                                        <strong style={{ fontWeight: 500 }}>{e.title || "Log entry"}</strong>
                                        <span style={{ fontSize: 11, color: "var(--bm-text-dim)" }}>
                                            {formatEncounterDate(e.date, e.date_to)}
                                        </span>
                                    </div>
                                    {e.notes && (
                                        <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--bm-text-muted)", lineHeight: 1.4 }}>
                                            {truncate(e.notes, 140)}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {err && (
                        <p data-testid="cruiser-contact-error" style={{ margin: "0 0 12px", color: "#e76f51", fontSize: 13 }}>
                            {err}
                        </p>
                    )}

                    {/* Actions */}
                    <div style={{
                        display: "flex", gap: 10, alignItems: "center",
                        flexWrap: "wrap", justifyContent: "space-between",
                        paddingTop: 8, borderTop: "1px solid var(--bm-border-soft)",
                        marginTop: 8,
                    }}>
                        {isEdit ? (
                            confirmDelete ? (
                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 12, color: "#e76f51" }}>Delete this contact?</span>
                                    <button
                                        data-testid="cc-confirm-delete-btn"
                                        type="button"
                                        onClick={doDelete}
                                        disabled={busy}
                                        className="bm-btn"
                                        style={{ background: "#e76f51", color: "#fff", fontSize: 12, padding: "8px 14px" }}
                                    >
                                        Yes, delete
                                    </button>
                                    <button
                                        data-testid="cc-cancel-delete-btn"
                                        type="button"
                                        onClick={() => setConfirmDelete(false)}
                                        className="bm-btn bm-btn-ghost"
                                        style={{ fontSize: 12 }}
                                    >
                                        Keep it
                                    </button>
                                </div>
                            ) : (
                                <button
                                    data-testid="cc-delete-btn"
                                    type="button"
                                    onClick={() => setConfirmDelete(true)}
                                    className="bm-btn bm-btn-ghost"
                                    style={{ color: "#e76f51", fontSize: 12 }}
                                >
                                    <Trash2 size={13} /> Delete
                                </button>
                            )
                        ) : <span />}

                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                data-testid="cc-cancel-btn"
                                type="button"
                                onClick={onClose}
                                className="bm-btn bm-btn-ghost"
                                disabled={busy}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="cc-save-btn"
                                type="submit"
                                disabled={busy}
                                className="bm-btn bm-btn-gold"
                                style={{ padding: "10px 18px", opacity: busy ? 0.6 : 1 }}
                            >
                                <Save size={13} /> {busy ? "Saving…" : isEdit ? "Save changes" : "Add to logbook"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

const twoCol = {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12,
};

const subCardStyle = {
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid var(--bm-border-soft)",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    display: "flex", flexDirection: "column", gap: 8,
};

function SectionHeader({ icon, label }) {
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 6,
            margin: "8px 0 10px",
            fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
            color: "var(--bm-gold)",
        }}>
            {icon} {label}
        </div>
    );
}

function formatEncounterDate(from, to) {
    if (!from && !to) return "";
    const fmt = (iso) => {
        try {
            return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
        } catch (_e) {
            return iso;
        }
    };
    if (from && to && from !== to) return `${fmt(from)} → ${fmt(to)}`;
    return fmt(from || to);
}

function truncate(s, n) {
    if (!s) return "";
    if (s.length <= n) return s;
    return s.slice(0, n - 1).trimEnd() + "…";
}
